import { Link } from "zudoku/components";
import { cn } from "zudoku/ui/util";
import {
  buildMethods,
  prefetchTutorial,
  stepHref,
  tutorialHref,
  tutorialSteps,
} from "./data.js";

interface MethodTabsProps {
  /** Build id from `BUILDS`, e.g. "gateway". */
  build: string;
  /** Method id from `METHODS`, e.g. "portal". */
  method: string;
  /**
   * Step slug, on a split tutorial. Switching method holds the reader's place
   * when the other path has the same step — someone on "Add rate limiting" in
   * the Portal who switches to the CLI wants rate limiting in the CLI, not the
   * top of the tutorial.
   */
  step?: string;
}

/**
 * The strip that switches which of a build's tutorials you are reading.
 *
 * Sits directly under the wizard's progress bar on every tutorial page, so
 * the control is in the same place wherever a reader lands.
 */
export function MethodTabs({ build, method, step }: MethodTabsProps) {
  const methods = buildMethods(build);
  if (methods.length < 2) return null;
  const hrefFor = (targetMethod: string) => {
    const steps = tutorialSteps(build, targetMethod);
    const sameStep = step && steps?.some((s) => s.slug === step);
    return sameStep
      ? stepHref(build, targetMethod, step)
      : tutorialHref(build, targetMethod);
  };

  return (
    <div className="not-prose mb-8">
      <p className="mb-2 text-xs font-medium text-muted-foreground">
        Follow along using
      </p>
      <div
        // Named so the strip slides its active pill to the newly selected
        // method rather than blinking out with the old page.
        style={{ viewTransitionName: "wizard-methods" }}
        className="inline-flex max-w-full flex-wrap gap-1 rounded-lg border border-border bg-muted/20 p-1"
        role="navigation"
        aria-label="Build method"
      >
        {methods.map((m) => {
          const MethodIcon = m.icon;
          const isActive = m.id === method;
          return (
            <Link
              key={m.id}
              to={hrefFor(m.id)}
              viewTransition
              onMouseEnter={() => prefetchTutorial(build, m.id)}
              onFocus={() => prefetchTutorial(build, m.id)}
              onTouchStart={() => prefetchTutorial(build, m.id)}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "inline-flex items-center gap-2 px-3 py-2 text-[13px] rounded-md no-underline transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                // Neutral, unlike the step rail. This strip switches which
                // tutorial you are reading; the rail tracks where you are
                // inside it. Painting both brand pink would claim they answer
                // the same question.
                isActive
                  ? "font-semibold text-foreground bg-background shadow-sm"
                  : "font-medium text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              <span aria-hidden="true">
                <MethodIcon size={15} />
              </span>
              {m.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
